import { FilesUploader } from "components";
import { MediaDirectoryKeys, MediaType } from "constants/enums";
import { DirectUploadMedia } from "libs/directUpload";
import {
  DriverCertificateType,
  useCreateDriver,
  useGetDriverById,
  useUpdateDriver
} from "modules/drivers";
import { useEffect, useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm
} from "react-hook-form";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  DateInput,
  FormField,
  Input,
  MediaFilePicker,
  Modal,
  MultipleMediaFilesPicker
} from "ui";

import styles from "./CompanyAddDriverModal.module.scss";

const FORM_ID = "COMPANY_DRIVER_FORM";

interface CertificateFieldValues {
  reference: string;
  images: DirectUploadMedia[];
}

interface CompanyDriverFormValues {
  firstname: string;
  lastname: string;
  phoneNumber: string;
  email: string;
  birthdate: Date | null;
  address: string;
  password: string;
  repeatPassword: string;
  image: DirectUploadMedia | null;
  driverLicense: CertificateFieldValues;
  insurance: CertificateFieldValues;
  idLicense: CertificateFieldValues;
  vehicle: CertificateFieldValues;
}

const EMPTY_VALUES: CompanyDriverFormValues = {
  firstname: "",
  lastname: "",
  phoneNumber: "",
  email: "",
  birthdate: null,
  address: "",
  password: "",
  repeatPassword: "",
  image: null,
  driverLicense: { reference: "", images: [] },
  insurance: { reference: "", images: [] },
  idLicense: { reference: "", images: [] },
  vehicle: { reference: "", images: [] }
};

interface CompanyAddDriverModalProps {
  isOpen: boolean;
  companyId: string;
  editableDriverId?: string | null;
  onClose: () => void;
  onCreated?: () => void;
}

const CERTIFICATE_FIELDS: Array<{
  name: keyof Pick<
    CompanyDriverFormValues,
    "driverLicense" | "insurance" | "idLicense" | "vehicle"
  >;
  label: string;
  numberLabel: string;
  type: DriverCertificateType;
}> = [
  {
    name: "driverLicense",
    label: "Driver license",
    numberLabel: "License ID",
    type: DriverCertificateType.DriverLicense
  },
  {
    name: "insurance",
    label: "Insurance",
    numberLabel: "Insurance number",
    type: DriverCertificateType.Insurance
  },
  {
    name: "idLicense",
    label: "ID",
    numberLabel: "ID number",
    type: DriverCertificateType.Id
  },
  {
    name: "vehicle",
    label: "Vehicle",
    numberLabel: "Plate number",
    type: DriverCertificateType.Vehicle
  }
];

const CompanyAddDriverModal = ({
  isOpen,
  companyId,
  editableDriverId,
  onClose,
  onCreated
}: CompanyAddDriverModalProps) => {
  const { t } = useTranslation();
  const isEdit = !!editableDriverId;

  const { driver: existingDriver, isLoading: isDriverLoading } =
    useGetDriverById(editableDriverId ?? "", { enabled: isEdit });

  const form = useForm<CompanyDriverFormValues>({
    defaultValues: EMPTY_VALUES
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = form;

  const passwordValue = watch("password");
  const imageValue = watch("image");

  useEffect(() => {
    if (!isOpen) return;

    if (isEdit && existingDriver) {
      reset({
        firstname: existingDriver.firstname,
        lastname: existingDriver.lastname,
        phoneNumber: existingDriver.phoneNumber,
        email: existingDriver.email ?? "",
        birthdate: existingDriver.birthdate
          ? new Date(existingDriver.birthdate)
          : null,
        address: existingDriver.address ?? "",
        password: "",
        repeatPassword: "",
        image: existingDriver.image ?? null,
        driverLicense: {
          reference: existingDriver.driverLicense?.reference ?? "",
          images: existingDriver.driverLicense?.images ?? []
        },
        insurance: {
          reference: existingDriver.insurance?.reference ?? "",
          images: existingDriver.insurance?.images ?? []
        },
        idLicense: {
          reference: existingDriver.idLicense?.reference ?? "",
          images: existingDriver.idLicense?.images ?? []
        },
        vehicle: {
          reference: existingDriver.vehicle?.reference ?? "",
          images: existingDriver.vehicle?.images ?? []
        }
      });
    } else if (!isEdit) {
      reset(EMPTY_VALUES);
    }
  }, [isOpen, isEdit, existingDriver, reset]);

  const handleAvatarUploaded = (media: DirectUploadMedia) =>
    setValue("image", media);
  const handleAvatarRemoved = () => setValue("image", null);

  const buildCertificates = (values: CompanyDriverFormValues) =>
    CERTIFICATE_FIELDS.map((cert) => {
      const fieldValues = values[cert.name];
      return {
        type: cert.type,
        reference: fieldValues.reference,
        imagesIds: fieldValues.images.map((m) => m.id)
      };
    }) as Parameters<typeof createMutation.mutate>[0]["certificates"];

  const handleSuccess = (message: string) => {
    toast.success(message);
    reset(EMPTY_VALUES);
    onCreated?.();
    onClose();
  };

  const handleError = (error: unknown) => {
    const raw = (
      error as { response?: { data?: { message?: string | string[] } } }
    )?.response?.data?.message;
    const message = Array.isArray(raw) ? raw.join(", ") : raw;
    toast.error(message ?? t("Failed to save driver"));
  };

  const createMutation = useCreateDriver({
    meta: { preventDefaultBadRequest: true },
    onSuccess: () => handleSuccess(t("Driver was created successfully")),
    onError: handleError
  });

  const updateMutation = useUpdateDriver({
    meta: { preventDefaultBadRequest: true },
    onSuccess: () => handleSuccess(t("Driver was updated successfully")),
    onError: handleError
  });

  const onSubmit = (values: CompanyDriverFormValues) => {
    if (isEdit && editableDriverId) {
      updateMutation.mutate({
        driverId: editableDriverId,
        firstname: values.firstname.trim(),
        lastname: values.lastname.trim(),
        phoneNumber: values.phoneNumber.replace(/\s/g, ""),
        email: values.email.trim() || null,
        address: values.address,
        birthdate: values.birthdate
          ? values.birthdate.toISOString()
          : undefined,
        imageId: values.image?.id ?? null,
        password: values.password ? values.password : undefined,
        repeatPassword: values.repeatPassword
          ? values.repeatPassword
          : undefined,
        certificates: buildCertificates(values),
        deliveryCompanyId: companyId
      });
    } else {
      createMutation.mutate({
        firstname: values.firstname.trim(),
        lastname: values.lastname.trim(),
        phoneNumber: values.phoneNumber.replace(/\s/g, ""),
        email: values.email.trim() || undefined,
        address: values.address,
        birthdate: values.birthdate ? values.birthdate.toISOString() : null,
        imageId: values.image?.id,
        password: values.password,
        repeatPassword: values.repeatPassword,
        priority: 1,
        capacity: 0,
        totalCargoWeight: 100,
        totalCargoVolume: 1,
        deliveryZonesIds: [],
        deliveryCompanyId: companyId,
        certificates: buildCertificates(values)
      });
    }
  };

  const isSubmitting = createMutation.isLoading || updateMutation.isLoading;

  return (
    <FormProvider {...form}>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEdit ? t("Edit Driver") : t("New Driver")}
        formId={FORM_ID}
        okText={isEdit ? t("Save changes") : t("Add driver")}
        cancelText={t("Cancel")}
        isLoading={isSubmitting || (isEdit && isDriverLoading)}
        className={styles.driverForm__modal}
      >
        <form
          id={FORM_ID}
          onSubmit={handleSubmit(onSubmit)}
          className={styles.driverForm}
        >
          <div className={styles.driverForm__avatarBlock}>
            <FilesUploader
              onSuccessUploading={handleAvatarUploaded}
              uploadDirectoryKey={MediaDirectoryKeys.InventoryManager}
            >
              {(uploaderProps) => (
                <MediaFilePicker
                  allowedMediaTypes={[MediaType.IMAGE]}
                  size="md"
                  variant="circle"
                  customSize={{ width: 100, height: 100 }}
                  {...uploaderProps}
                  value={imageValue}
                  onDeleteMediaFile={handleAvatarRemoved}
                  data-testid="driver--avatar"
                />
              )}
            </FilesUploader>
            <span className={styles.driverForm__avatarHint}>
              {t("Driver photo")}
            </span>
          </div>

          <div className={styles.driverForm__row}>
            <FormField
              label={t("First name")}
              errorMessage={errors.firstname?.message}
            >
              <Input
                {...register("firstname", {
                  required: t("Required field")
                })}
                placeholder={t("First name")}
              />
            </FormField>
            <FormField
              label={t("Last name")}
              errorMessage={errors.lastname?.message}
            >
              <Input
                {...register("lastname", {
                  required: t("Required field")
                })}
                placeholder={t("Last name")}
              />
            </FormField>
          </div>

          <FormField
            label={t("Phone")}
            errorMessage={errors.phoneNumber?.message}
          >
            <Input
              {...register("phoneNumber", {
                required: t("Required field")
              })}
              placeholder="+225 27 22 12 34 56"
            />
          </FormField>

          <div className={styles.driverForm__row}>
            <Controller
              control={control}
              name="birthdate"
              rules={{ required: t("Required field") }}
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <DateInput
                  label={t("Birthdate")}
                  value={value}
                  onChange={onChange}
                  errorMessage={error?.message}
                  maxDate={new Date()}
                />
              )}
            />
            <FormField
              label={t("Address")}
              errorMessage={errors.address?.message}
            >
              <Input
                {...register("address", {
                  required: t("Required field"),
                  minLength: {
                    value: 5,
                    message: t("Min 5 characters")
                  }
                })}
                placeholder={t("Plateau, Abidjan")}
              />
            </FormField>
          </div>

          <FormField label={t("Email")} errorMessage={errors.email?.message}>
            <Input
              type="email"
              {...register("email")}
              placeholder="driver@example.com"
            />
          </FormField>

          <div className={styles.driverForm__row}>
            <FormField
              label={t("Password")}
              errorMessage={errors.password?.message}
            >
              <Input
                isPassword
                {...register("password", {
                  required: isEdit ? false : t("Required field"),
                  minLength: { value: 8, message: t("Min 8 characters") }
                })}
                placeholder={isEdit ? t("Leave blank to keep") : "••••••••"}
              />
            </FormField>
            <FormField
              label={t("Repeat Password")}
              errorMessage={errors.repeatPassword?.message}
            >
              <Input
                isPassword
                {...register("repeatPassword", {
                  required: isEdit
                    ? false
                    : passwordValue
                      ? t("Required field")
                      : false,
                  validate: (value) =>
                    !passwordValue ||
                    value === passwordValue ||
                    t("Passwords do not match")
                })}
                placeholder="••••••••"
              />
            </FormField>
          </div>

          {CERTIFICATE_FIELDS.map((cert) => (
            <CertificateBlock
              key={cert.name}
              control={control}
              fieldName={cert.name}
              label={cert.label}
              numberLabel={cert.numberLabel}
              referenceError={errors[cert.name]?.reference?.message}
            />
          ))}
        </form>
      </Modal>
    </FormProvider>
  );
};

interface CertificateBlockProps {
  control: ReturnType<typeof useForm<CompanyDriverFormValues>>["control"];
  fieldName: "driverLicense" | "insurance" | "idLicense" | "vehicle";
  label: string;
  numberLabel: string;
  referenceError?: string;
}

const CertificateBlock = ({
  control,
  fieldName,
  label,
  numberLabel,
  referenceError
}: CertificateBlockProps) => {
  const { t } = useTranslation();

  const {
    fields: mediaFiles,
    append: appendMediaFile,
    remove: removeMediaFile
  } = useFieldArray<CompanyDriverFormValues>({
    control,
    name: `${fieldName}.images` as never
  });

  return (
    <div className={styles.driverForm__certBlock}>
      <h4 className={styles.driverForm__certBlock__title}>{t(label)}</h4>
      <div className={styles.driverForm__certInputs}>
        <Controller
          control={control}
          name={`${fieldName}.reference`}
          rules={{ required: t("Required field") }}
          defaultValue=""
          render={({ field: { value, onChange } }) => (
            <Input
              value={value ?? ""}
              onChange={onChange}
              label={t(numberLabel)}
              errorMessage={referenceError}
              maxLength={25}
            />
          )}
        />

        <Controller
          control={control}
          name={`${fieldName}.images`}
          render={({ fieldState: { error } }) => (
            <FormField
              label={`${t(label)} ${t("Photo")}`}
              errorMessage={error?.message}
            >
              <FilesUploader
                onSuccessUploading={appendMediaFile}
                uploadDirectoryKey={MediaDirectoryKeys.InventoryManager}
              >
                {(uploaderProps) => (
                  <MultipleMediaFilesPicker
                    allowedMediaTypes={[MediaType.IMAGE, MediaType.DOCUMENT]}
                    size="md"
                    {...uploaderProps}
                    value={mediaFiles as unknown as DirectUploadMedia[]}
                    onDeleteMediaFile={removeMediaFile}
                  />
                )}
              </FilesUploader>
            </FormField>
          )}
        />
      </div>
    </div>
  );
};

export default CompanyAddDriverModal;
